import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import EmptyState from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  LayoutTemplate, Plus, Pencil, Copy, ToggleLeft, ToggleRight,
  Thermometer, SprayCan, FileText, AlertTriangle, Trash2, Loader2,
  Globe, Building2, ChevronDown, ChevronUp,
} from "lucide-react";
import {
  BUSINESS_TYPES, DEFAULT_TEMPLATES, ZONE_LABELS, EQUIPMENT_TYPE_LABELS, FREQUENCY_LABELS,
} from "@/components/templates/BUSINESS_TEMPLATES";

const SECTION_ICONS = {
  temperature_equipment: Thermometer,
  cleaning_tasks: SprayCan,
  document_categories: FileText,
  incident_categories: AlertTriangle,
};

export default function Templates() {
  const { currentVenue, userRole, loading: venueLoading } = useVenue();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [tab, setTab] = useState("global");

  const [form, setForm] = useState({
    name: "", description: "", business_type: "restaurant", is_global: true,
    active: true, notes: "",
    temperature_equipment: [], cleaning_tasks: [], document_categories: [], incident_categories: [],
  });

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    setLoading(true);
    const all = await hosys.entities.BusinessTemplate.list();
    setTemplates(all);
    setLoading(false);
  };

  const openNew = () => {
    setEditingTemplate(null);
    setForm({
      name: "", description: "", business_type: "restaurant", is_global: true,
      active: true, notes: "",
      temperature_equipment: [...(DEFAULT_TEMPLATES.restaurant.temperature_equipment || [])],
      cleaning_tasks: [...(DEFAULT_TEMPLATES.restaurant.cleaning_tasks || [])],
      document_categories: [...(DEFAULT_TEMPLATES.restaurant.document_categories || [])],
      incident_categories: [...(DEFAULT_TEMPLATES.restaurant.incident_categories || [])],
    });
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditingTemplate(t);
    setForm({
      name: t.name, description: t.description || "", business_type: t.business_type,
      is_global: t.is_global ?? true, active: t.active ?? true, notes: t.notes || "",
      temperature_equipment: t.temperature_equipment || [],
      cleaning_tasks: t.cleaning_tasks || [],
      document_categories: t.document_categories || [],
      incident_categories: t.incident_categories || [],
    });
    setShowForm(true);
  };

  const handleTypeChange = (type) => {
    const defaults = DEFAULT_TEMPLATES[type] || {};
    setForm(f => ({
      ...f, business_type: type,
      temperature_equipment: defaults.temperature_equipment || [],
      cleaning_tasks: defaults.cleaning_tasks || [],
      document_categories: defaults.document_categories || [],
      incident_categories: defaults.incident_categories || [],
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    const data = { ...form, venue_id: form.is_global ? null : currentVenue?.id };
    if (editingTemplate) {
      await hosys.entities.BusinessTemplate.update(editingTemplate.id, data);
    } else {
      await hosys.entities.BusinessTemplate.create(data);
    }
    setShowForm(false);
    await loadTemplates();
    setSaving(false);
  };

  const handleDuplicate = async (t) => {
    setSaving(true);
    await hosys.entities.BusinessTemplate.create({
      ...t, id: undefined, name: `${t.name} (còpia)`, created_date: undefined, updated_date: undefined,
    });
    await loadTemplates();
    setSaving(false);
  };

  const handleToggle = async (t) => {
    await hosys.entities.BusinessTemplate.update(t.id, { active: !t.active });
    await loadTemplates();
  };

  const handleDelete = async (t) => {
    await hosys.entities.BusinessTemplate.delete(t.id);
    await loadTemplates();
  };

  const isAdmin = userRole === "superadmin" || userRole === "admin";

  if (venueLoading || loading) return <QuickLoader />;

  if (!isAdmin) {
    return (
      <EmptyState
        icon={LayoutTemplate}
        title="Accés restringit"
        description="Necessites permisos d'administrador."
      />
    );
  }

  const globalTemplates = templates.filter(t => t.is_global);
  const venueTemplates  = templates.filter(t => !t.is_global && t.venue_id === currentVenue?.id);
  const displayList     = tab === "global" ? globalTemplates : venueTemplates;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Plantilles de Configuració"
        subtitle="Gestiona les plantilles base per a cada tipus de negoci"
        actions={
          <Button onClick={openNew} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2 rounded-xl">
            <Plus className="w-4 h-4" /> Nova plantilla
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-gray-200 rounded-xl p-1">
          <TabsTrigger value="global" className="rounded-lg gap-2 data-[state=active]:bg-[#7B2D3F] data-[state=active]:text-white">
            <Globe className="w-4 h-4" /> Globals AEH Reus ({globalTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="local" className="rounded-lg gap-2 data-[state=active]:bg-[#7B2D3F] data-[state=active]:text-white">
            <Building2 className="w-4 h-4" /> Del Local ({venueTemplates.length})
          </TabsTrigger>
        </TabsList>

        {["global", "local"].map(tabKey => (
          <TabsContent key={tabKey} value={tabKey} className="space-y-3 mt-4">
            {displayList.length === 0 ? (
              <EmptyState
                icon={LayoutTemplate}
                title="Cap plantilla"
                description={tabKey === "global" ? "Crea plantilles globals per activar-les als locals." : "Crea plantilles específiques per a aquest local."}
                action={<Button onClick={openNew} className="bg-[#7B2D3F] hover:bg-[#5B1D2F] gap-2"><Plus className="w-4 h-4" /> Crear plantilla</Button>}
              />
            ) : (
              displayList.map(t => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  expanded={expandedId === t.id}
                  onToggleExpand={() => setExpandedId(expandedId === t.id ? null : t.id)}
                  onEdit={() => openEdit(t)}
                  onDuplicate={() => handleDuplicate(t)}
                  onToggleActive={() => handleToggle(t)}
                  onDelete={() => handleDelete(t)}
                  saving={saving}
                />
              ))
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "Editar plantilla" : "Nova plantilla"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nom *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nom de la plantilla" />
              </div>
              <div>
                <Label>Tipus de negoci</Label>
                <Select value={form.business_type} onValueChange={handleTypeChange}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(BUSINESS_TYPES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.emoji} {v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Descripció</Label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Breu descripció de la plantilla" />
            </div>
            <div className="flex items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.is_global} onChange={e => setForm({ ...form, is_global: e.target.checked })} className="rounded" />
                Plantilla global (AEH Reus)
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="rounded" />
                Activa
              </label>
            </div>

            {/* Equipment preview */}
            <FormSection
              title={`Equips de temperatura (${form.temperature_equipment.length})`}
              icon={Thermometer}
            >
              <div className="space-y-1">
                {form.temperature_equipment.map((eq, i) => (
                  <div key={eq.name || i} className="flex items-center justify-between text-sm bg-blue-50 rounded-lg px-3 py-2">
                    <span className="font-medium">{eq.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">{EQUIPMENT_TYPE_LABELS[eq.equipment_type] || eq.equipment_type}</span>
                      <span className="text-blue-700 font-mono">{eq.min_limit}° / {eq.max_limit}°</span>
                    </div>
                  </div>
                ))}
              </div>
            </FormSection>

            <FormSection
              title={`Tasques de neteja (${form.cleaning_tasks.length})`}
              icon={SprayCan}
            >
              <div className="space-y-1">
                {form.cleaning_tasks.map((t, i) => (
                  <div key={t.task || i} className="flex items-center justify-between text-sm bg-green-50 rounded-lg px-3 py-2">
                    <span className="font-medium">{t.task}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">{ZONE_LABELS[t.zone] || t.zone}</Badge>
                      <Badge className="text-xs bg-green-100 text-green-700 border-0">{FREQUENCY_LABELS[t.frequency] || t.frequency}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            </FormSection>

            <FormSection title={`Documents recomanats (${form.document_categories.length})`} icon={FileText}>
              <div className="flex flex-wrap gap-2">
                {form.document_categories.map((d, i) => (
                  <Badge key={d || i} variant="outline" className="text-xs">{d}</Badge>
                ))}
              </div>
            </FormSection>

            <FormSection title={`Categories d'incidències (${form.incident_categories.length})`} icon={AlertTriangle}>
              <div className="flex flex-wrap gap-2">
                {form.incident_categories.map((d, i) => (
                  <Badge key={d || i} className="text-xs bg-orange-100 text-orange-700 border-0">{d}</Badge>
                ))}
              </div>
            </FormSection>

            <div>
              <Label>Notes internes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Observacions, restriccions, etc." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel·lar</Button>
            <Button onClick={handleSave} disabled={!form.name || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingTemplate ? "Actualitzar" : "Crear plantilla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormSection({ title, icon: Icon, children }) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">{title}</span>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function TemplateCard({ template: t, expanded, onToggleExpand, onEdit, onDuplicate, onToggleActive, onDelete, saving }) {
  const bt = BUSINESS_TYPES[t.business_type] || { label: t.business_type, emoji: "📋" };
  return (
    <Card className={`border-0 shadow-sm overflow-hidden transition-all ${!t.active ? "opacity-60" : ""}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center text-lg flex-shrink-0">
              {bt.emoji}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-gray-900 truncate">{t.name}</h3>
                <Badge variant="secondary" className="text-xs">{bt.label}</Badge>
                {t.is_global && <Badge className="text-xs bg-blue-100 text-blue-700 border-0"><Globe className="w-3 h-3 mr-1 inline" />Global</Badge>}
                {t.active ? (
                  <Badge className="text-xs bg-emerald-100 text-emerald-700 border-0">Activa</Badge>
                ) : (
                  <Badge variant="secondary" className="text-xs">Inactiva</Badge>
                )}
              </div>
              {t.description && <p className="text-xs text-gray-500 mt-0.5 truncate">{t.description}</p>}
              <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                <span>{t.temperature_equipment?.length || 0} equips</span>
                <span>·</span>
                <span>{t.cleaning_tasks?.length || 0} tasques neteja</span>
                <span>·</span>
                <span>{t.document_categories?.length || 0} docs</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            <Button variant="ghost" size="icon" onClick={onToggleExpand} className="w-8 h-8">
              {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={onEdit} className="w-8 h-8">
              <Pencil className="w-4 h-4 text-gray-400" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onDuplicate} disabled={saving} className="w-8 h-8">
              <Copy className="w-4 h-4 text-gray-400" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onToggleActive} className="w-8 h-8">
              {t.active ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={onDelete} className="w-8 h-8">
              <Trash2 className="w-4 h-4 text-red-400" />
            </Button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
            {(t.temperature_equipment || []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5"><Thermometer className="w-3.5 h-3.5" /> Equips de temperatura</p>
                <div className="space-y-1">
                  {t.temperature_equipment.map((eq, i) => (
                    <div key={`entry-${i}`} className="flex items-center justify-between text-xs bg-blue-50 rounded-lg px-3 py-1.5">
                      <span className="font-medium text-gray-700">{eq.name}</span>
                      <span className="text-blue-700 font-mono">{eq.min_limit}° – {eq.max_limit}°C</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(t.cleaning_tasks || []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5"><SprayCan className="w-3.5 h-3.5" /> Tasques de neteja</p>
                <div className="space-y-1">
                  {t.cleaning_tasks.map((ct, i) => (
                    <div key={`ct-${i}`} className="flex items-center justify-between text-xs bg-green-50 rounded-lg px-3 py-1.5">
                      <span className="font-medium text-gray-700">{ct.task}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{ZONE_LABELS[ct.zone] || ct.zone}</Badge>
                        <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border-0">{FREQUENCY_LABELS[ct.frequency] || ct.frequency}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(t.document_categories || []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Documents</p>
                <div className="flex flex-wrap gap-1.5">
                  {t.document_categories.map((d, i) => <Badge key={`day-${i}`} variant="outline" className="text-[10px]">{d}</Badge>)}
                </div>
              </div>
            )}
            {t.notes && <p className="text-xs text-gray-400 italic">{t.notes}</p>}
          </div>
        )}
      </div>
    </Card>
  );
}